import React, { lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Context Providers
import { AuthProvider, useAuth } from './context/AuthContext';
import { SchemaProvider } from './context/SchemaContext';
import { NotificationProvider } from './context/NotificationContext';
import { I18nProvider } from './context/I18nContext';
import { ToastProvider } from './context/ToastContext';

// Components for route protection and layout
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Layout from './components/Layout';

// Public page
const Welcome = lazy(() => import('./pages/Welcome'));
const Login = lazy(() => import('./pages/Login'));

// Protected pages
const Home = lazy(() => import('./pages/Home'));

// Error pages
const NotFound = lazy(() => import('./pages/NotFound'));
const ServerError = lazy(() => import('./pages/ServerError'));

// Form Pages - 使用新的精簡版組件
const FormSelector = lazy(() => import('./pages/Form/FormSelector'));
const FormEditor = lazy(() => import('./pages/Form/FormEditor'));
const FormView = lazy(() => import('./pages/Form/FormView'));
const FormSearch = lazy(() => import('./pages/Form/FormSearch'));
const FormApprovalList = lazy(() => import('./pages/Form/FormApprovalList'));
const FormApprovalDetail = lazy(() => import('./pages/Form/FormApproval'));
const FormPrint = lazy(() => import('./pages/Form/FormPrint'));

// User Pages
const UserProfile = lazy(() => import('./pages/UserProfile'));

// Development & Admin Pages (管理員)
const DevPanel = lazy(() => import('./pages/Setting/DevPanel'));
const Settings = lazy(() => import('./pages/Setting/Settings'));
const ConfigManager = lazy(() => import('./pages/Setting/ConfigManager'));
const WorkflowDesigner = lazy(() => import('./pages/Setting/WorkflowDesigner'));
const NotificationTest = lazy(() => import('./pages/Setting/NotificationTest'));
const TemplateMaintenance = lazy(() => import('./pages/Setting/TemplateMaintenance'));

// Personal Reminders Pages
const ReminderList = lazy(() => import('./pages/Reminder/ReminderList'));
const ReminderEditor = lazy(() => import('./pages/Reminder/ReminderEditor'));
const ReminderView = lazy(() => import('./pages/Reminder/ReminderView'));

/**
 * AuthAwareSchemaProvider - Wrapper that passes auth state to SchemaProvider
 * This ensures schema is only loaded after authentication
 */
const AuthAwareSchemaProvider = ({ children }) => {
    const { authenticated } = useAuth();
    return (
        <SchemaProvider isAuthenticated={authenticated}>
            {children}
        </SchemaProvider>
    );
};

const App = () => {
    return (
        <BrowserRouter> {/* Router for SPA navigation */}
            <I18nProvider> {/* Internationalization Context */}
                <ToastProvider> {/* Toast Notification Context */}
                    <AuthProvider> {/* Authentication Context */}
                        <AuthAwareSchemaProvider> {/* Schema Context dependent on Auth */}
                            <NotificationProvider position="top-right"> {/* Notification Context */}
                                <Routes>
                                    {/* 公开路由 */}
                                    <Route path="/" element={<Layout><Welcome /></Layout>} />
                                    <Route path="/login" element={<Layout><Login /></Layout>} />

                                    {/* 受保护的路由 */}
                                    <Route
                                        path="/home"
                                        element={
                                            <ProtectedRoute>
                                                <Layout showHeader><Home /></Layout>
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* 表单系统路由 */}
                                    {/* 表单选择页面 */}
                                    <Route
                                        path="/forms"
                                        element={
                                            <ProtectedRoute>
                                                <Layout showHeader><FormSelector /></Layout>
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* 新建表单 */}
                                    <Route
                                        path="/forms/new/:formId"
                                        element={
                                            <ProtectedRoute>
                                                <Layout showHeader><FormEditor /></Layout>
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* 编辑表单（草稿） */}
                                    <Route
                                        path="/forms/edit/:applicationId"
                                        element={
                                            <ProtectedRoute>
                                                <Layout showHeader><FormEditor /></Layout>
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* 查看表单 */}
                                    <Route
                                        path="/forms/view/:applicationId"
                                        element={
                                            <ProtectedRoute>
                                                <Layout showHeader><FormView /></Layout>
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* 表单查询列表 */}
                                    <Route
                                        path="/forms/search"
                                        element={
                                            <ProtectedRoute>
                                                <Layout showHeader><FormSearch /></Layout>
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* 待审核表单列表 */}
                                    <Route
                                        path="/forms/approval"
                                        element={
                                            <ProtectedRoute>
                                                <Layout showHeader><FormApprovalList /></Layout>
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* 单个表单审批页面 */}
                                    <Route
                                        path="/forms/approval/:applicationId"
                                        element={
                                            <ProtectedRoute>
                                                <Layout showHeader><FormApprovalDetail /></Layout>
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* 打印表单 */}
                                    <Route
                                        path="/forms/print/:applicationId"
                                        element={
                                            <ProtectedRoute>
                                                <FormPrint />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* 用户相关路由 */}
                                    <Route
                                        path="/profile"
                                        element={
                                            <ProtectedRoute>
                                                <Layout showHeader><UserProfile /></Layout>
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* 开发者工具 (僅管理員) */}
                                    <Route
                                        path="/dev"
                                        element={
                                            <AdminRoute>
                                                <Layout showHeader><DevPanel /></Layout>
                                            </AdminRoute>
                                        }
                                    />

                                    {/* 通知測試頁面 (僅管理員) */}
                                    <Route
                                        path="/dev/notifications"
                                        element={
                                            <AdminRoute>
                                                <Layout showHeader><NotificationTest /></Layout>
                                            </AdminRoute>
                                        }
                                    />

                                    {/* 系統設定 (僅管理員) */}
                                    <Route
                                        path="/settings"
                                        element={
                                            <AdminRoute>
                                                <Layout showHeader><Settings /></Layout>
                                            </AdminRoute>
                                        }
                                    />

                                    {/* 模板維護 (管理員) */}
                                    <Route
                                        path="/settings/templates"
                                        element={
                                            <AdminRoute>
                                                <Layout showHeader><TemplateMaintenance /></Layout>
                                            </AdminRoute>
                                        }
                                    />

                                    {/* 系統配置管理 (管理員) */}
                                    <Route
                                        path="/config"
                                        element={
                                            <AdminRoute>
                                                <Layout showHeader><ConfigManager /></Layout>
                                            </AdminRoute>
                                        }
                                    />

                                    {/* 工作流程設計器 (管理員) */}
                                    <Route
                                        path="/workflow-designer"
                                        element={
                                            <AdminRoute>
                                                <Layout showHeader><WorkflowDesigner /></Layout>
                                            </AdminRoute>
                                        }
                                    />

                                    {/* 個人提醒功能 */}
                                    <Route
                                        path="/reminders"
                                        element={
                                            <ProtectedRoute>
                                                <Layout showHeader><ReminderList /></Layout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/reminders/new"
                                        element={
                                            <ProtectedRoute>
                                                <Layout showHeader><ReminderEditor /></Layout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/reminders/edit/:reminderId"
                                        element={
                                            <ProtectedRoute>
                                                <Layout showHeader><ReminderEditor /></Layout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/reminders/view/:reminderId"
                                        element={
                                            <ProtectedRoute>
                                                <Layout showHeader><ReminderView /></Layout>
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* 错误页面 */}
                                    <Route path="/500" element={<Layout><ServerError /></Layout>} />
                                    <Route path="/404" element={<Layout><NotFound /></Layout>} />
                                    <Route path="*" element={<Navigate to="/404" replace />} />
                                </Routes>
                            </NotificationProvider>
                        </AuthAwareSchemaProvider>
                    </AuthProvider>
                </ToastProvider>
            </I18nProvider>
        </BrowserRouter>
    );
};

export default App;
